import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Loader2, Folder, FileText, CheckCircle2, ShieldAlert, AlertCircle, PlayCircle, ExternalLink, XCircle, Ban, Copy, Send, Clock } from 'lucide-react';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';

export function ExecutionCommitteeDashboard() {
  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats');
      return res.data.data;
    },
  });

  return (
    <Layout title="Execution Committee Portal">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">DOM680 Dashboard</h2>
          <p className="text-slate-500 text-sm">Review, prioritize and moderate idea workflow</p>
        </div>
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
          {/* Total Ideas */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600 shrink-0">
                <Folder className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Total Ideas</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.totalIdeas || 0}</p>
          </div>


          {/* Reviewed */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-orange-50 p-1.5 rounded-lg text-orange-600 shrink-0">
                <Clock className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Reviewed</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.underEvaluation || 0}</p>
          </div>

          {/* Yet to Be Reviewed */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                <Send className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Yet to Be Reviewed</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.submitted || 0}</p>
          </div>


          {/* Requested EC Head Approval */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate" title="Requested EC Head Approval">Requested EC Head Approval</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0 ml-1">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
          </div>

          {/* External Approved */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate" title="External Approved">External Approved</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0 ml-1">{globalStats?.kpis?.approved || 0}</p>
          </div>

          {/* External Rejected */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                <XCircle className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate" title="External Rejected">External Rejected</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0 ml-1">{globalStats?.kpis?.externalRejected || 0}</p>
          </div>
        </div>
      )}

      <IdeasFilterTable
        queryKeyPrefix="executionIdeas"
        apiEndpoint="/admin/ideas"
        showExport={true}
        allowDelete={false}
        actionButtonLabel="Evaluate"
      />
    </Layout>
  );
}
